import { type PaletteKey, type PixelCanvas, sprite } from '../../../lib';
import { burst, frames3, frames4, muzzle, paint, shot, type Tone } from './shared';

/**
 * Boogie Blaster's shots (the Disco set's gun, ADR-0160 amended): a glowing music note changing colour as it flies
 * (upright: a note never turns), a fan of coloured stage lights at the muzzle, a starburst of coloured glints where it
 * lands; and the flourish's dance-floor tile (`disco_tile`, white: tinted) and a mirror ball's shard (`disco_bit`).
 */

/** The note's colours a frame: [rim, body, shine]. */
const NOTE_TONES: readonly (readonly [PaletteKey, PaletteKey, PaletteKey])[] = [
  ['p', 'i', 'w'],
  ['Y', 'y', 'w'],
  ['g', 'l', 'w'],
  ['u', 'o', 'y'],
];

/**
 * An eighth note, its head round the origin (21, 8): a tilted oval, a stem up its right side, a flag curling off the
 * stem's top. 0 off it, 1 its rim, 2 its body, 3 its shine.
 */
function note(x: number, y: number): 0 | 1 | 2 | 3 {
  const [dx, dy] = [x - 20, y - 10.5];
  const [u, v] = [dx * 0.94 + dy * 0.34, -dx * 0.34 + dy * 0.94];
  const head = (u / 3.4) ** 2 + (v / 2.5) ** 2;
  if (head <= 1) return head < 0.35 && u < 0 && v < 0 ? 3 : head > 0.6 ? 1 : 2;
  // The stem.
  if (x >= 22 && x < 24 && y >= 1.5 && y < 10.5) return x < 23 ? 2 : 1;
  // The flag: off the stem's top, curling down to the right.
  const t = y - 1.5;
  if (t >= 0 && t < 6) {
    const from = 24;
    const to = 24 + 3.6 * Math.sin((t / 6) * Math.PI) + t * 0.25;
    if (x >= from && x < to) return x + 1 >= to ? 1 : 2;
  }
  return 0;
}

const flying = (f: number) => (c: PixelCanvas) => {
  const [rim, body, shine] = NOTE_TONES[f % NOTE_TONES.length] as readonly [PaletteKey, PaletteKey, PaletteKey];
  paint(c, (x, y) => {
    const k = note(x, y);
    return k === 0 ? null : k === 1 ? rim : k === 2 ? body : shine;
  });
};

/** The stage lights' colours, beam by beam. */
const BEAMS: readonly (readonly [PaletteKey, PaletteKey])[] = [
  ['i', 'p'],
  ['y', 'Y'],
  ['l', 'g'],
  ['w', '5'],
  ['o', 'u'],
];

/** A fan of five stage lights out of the muzzle, a white flash at its root; shorter and thinner frame by frame. */
const lights = (f: number) => (c: PixelCanvas) => {
  const reach = [38, 30, 20][f] as number;
  paint(c, (x, y) => {
    const [dx, dy] = [x - 4, y - 20];
    const d = Math.hypot(dx, dy);
    if (f < 2 && d < (f === 0 ? 5 : 3)) return d < 2.5 ? 'w' : 'y';
    if (dx <= 1 || d > reach) return null;
    const a = Math.atan2(dy, dx);
    for (const [i, [hi, lo]] of BEAMS.entries()) {
      const at = (i - 2) * 0.32 + (f === 1 ? 0.05 : f === 2 ? -0.05 : 0);
      const off = Math.abs(a - at) * d;
      const half = (0.6 + d * 0.07) * (f === 2 ? 0.6 : 1);
      if (off < half) return off < half * 0.45 && d < reach * 0.7 ? 'w' : d > reach * 0.75 ? lo : hi;
    }
    return null;
  });
};

/** A four-pointed glint at (cx, cy), its arms `r` long, in `hi` round a white heart. */
const glint = (x: number, y: number, cx: number, cy: number, r: number, hi: PaletteKey): Tone => {
  const [dx, dy] = [Math.abs(x - cx), Math.abs(y - cy)];
  if (dx < 1 && dy < 1) return 'w';
  if ((dx < 0.6 && dy < r) || (dy < 0.6 && dx < r)) return dx + dy < r * 0.45 ? 'w' : hi;
  if (dx + dy < 2.2) return hi;
  return null;
};

const GLINTS: readonly PaletteKey[] = ['i', 'y', 'l', 'w', 'o', 'p', 'y', 'l'];

/** Where it lands: a white starburst, then a ring of coloured glints flung out, flaring and going. */
const sparkle = (f: number) => (c: PixelCanvas) =>
  paint(c, (x, y) => {
    if (f === 0) return glint(x, y, 22, 22, 9, 'y') ?? glint(x, y, 22, 22, 5, 'i');
    const R = [0, 9.5, 14, 17][f] as number;
    const r = [0, 3, 2.6, 1.6][f] as number;
    for (const [i, hi] of GLINTS.entries()) {
      if (f === 3 && i % 2) continue;
      const a = (i / GLINTS.length) * Math.PI * 2 + 0.3;
      const k = glint(x, y, 22 + Math.cos(a) * R, 22 + Math.sin(a) * R * 0.85, r, hi);
      if (k) return k;
    }
    return f === 1 && Math.hypot(x - 22, y - 22) < 2 ? 'w' : null;
  });

/** A lit dance-floor tile: white, its rim a shade dimmer, a shine in its top-left corner (tinted its colour). */
const tile = (c: PixelCanvas) =>
  paint(c, (x, y) => {
    if (x < 1 || x > 15 || y < 1 || y > 11) return null;
    if (x < 2 || y < 2 || x > 14 || y > 10) return '5';
    return x + y < 7 ? 'w' : x + y > 20 ? '5' : 'w';
  });

/** A shard of the mirror ball turning: a facet face on, tilting, on its edge, tilting back; glinting face on. */
const shard = (f: number) => (c: PixelCanvas) =>
  paint(c, (x, y) => {
    const w = [4, 3, 1, 3][f] as number;
    if (Math.abs(x - 4) > w / 2 || Math.abs(y - 4) > 2) return null;
    if (f === 0) return x < 4 && y < 4 ? 'w' : '5';
    return f === 2 ? 'w' : x < 4 ? '5' : '4';
  });

export default [
  sprite({
    name: 'boogie_shot',
    ...shot,
    notes: `Boogie Blaster's bullet (ADR-0160 amended): an eighth note (a tilted oval head, a stem, a curling flag) glowing
in a new colour each frame: pink, gold, lime, orange (rim, body, a white shine on the head). Drawn upright (a note never
turns; SKIN_FX upright), its head on the pellet's origin. Rubric (4×): S5 R5 P4 L4 C4 A4 F4. Weakest: 12 px tall, at 1:1
it reads as a glowing coloured blob with a tick more than a note.`,
    anims: { idle: { fps: 10, frames: frames4(flying) } },
  }),
  sprite({
    name: 'boogie_muzzle',
    ...muzzle,
    notes: `Boogie Blaster's muzzle flash (ADR-0160 amended): a fan of five stage lights (pink, gold, lime, white, orange,
white-hot down their middles) out of a white flash at the muzzle, swinging a little, shorter and thinner frame by frame.
Points right. Rubric (4×): S4 R5 P4 L4 C4 A4 F4.`,
    anims: { flash: { fps: 30, loop: false, frames: frames3(lights) } },
  }),
  sprite({
    name: 'boogie_burst',
    ...burst,
    notes: `Boogie Blaster's hit (ADR-0160 amended): a white four-pointed starburst in gold and pink, then eight coloured
glints flung out in a ring, flaring smaller as they go, the last four. 4 frames. Rubric (4×): S4 R4 P4 L4 C4 A4 F4.`,
    anims: { burst: { fps: 20, loop: false, frames: frames4(sparkle) } },
  }),
  sprite({
    name: 'disco_tile',
    group: 'fx',
    size: [16, 12],
    origin: [8, 6],
    density: 4,
    notes: `The Disco's knock-out floor (ADR-0160 amended): one lit dance-floor tile, white with a rim a shade dimmer and a
shine in its top-left; the flourish tints each its colour and lights them in rings round the body. Rubric (4×): S4 R4 P5
L4 C5 A- F4.`,
    anims: { idle: { fps: 0, frames: [tile] } },
  }),
  sprite({
    name: 'disco_bit',
    group: 'fx',
    size: [8, 8],
    origin: [4, 4],
    density: 4,
    notes: `The Disco's knock-out shard (ADR-0160 amended): a facet of the mirror ball turning over (face on with a white
glint, tilting, on its edge flashing white, tilting back). Rubric (4×): S4 R4 P5 L4 C4 A4 F4.`,
    anims: { idle: { fps: 12, frames: frames4(shard) } },
  }),
];
